import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import apiService from '../../services/api';
import EmailComposer from './EmailComposer';

// Styled Components
const CustomersWrapper = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const CustomersHeader = styled.div`
  padding: var(--spacing-6);
  border-bottom: 1px solid var(--color-border);
  
  h2 {
    margin: 0 0 var(--spacing-4) 0;
    color: var(--color-secondary);
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
    font-size: var(--font-size-xl);
  }
  
  p {
    color: var(--color-text-light);
    margin: 0 0 var(--spacing-4) 0;
  }
`;

const ControlsWrapper = styled.div`
  display: flex;
  gap: var(--spacing-4);
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: var(--spacing-4);
`;

const SearchInput = styled.input`
  padding: var(--spacing-3) var(--spacing-4);
  border: 2px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  font-size: var(--font-size-base);
  width: 300px;
  background: #fafafa;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: #9ca3af;
    font-style: italic;
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    background: white;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: var(--spacing-3);
  margin-left: auto;
`;

const Button = styled.button`
  padding: var(--spacing-3) var(--spacing-4);
  border: none;
  border-radius: var(--border-radius);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  
  &.primary {
    background-color: var(--color-secondary);
    color: white;
    
    &:hover {
      background-color: #1f1e3a;
    }
    
    &:disabled {
      background-color: var(--color-text-light);
      cursor: not-allowed;
    }
  }
  
  &.secondary {
    background-color: var(--color-border);
    color: var(--color-text);
    
    &:hover {
      background-color: #e5e7eb;
    }
  }
  
  &.danger {
    background-color: #dc3545;
    color: white;
    font-size: var(--font-size-sm);
    padding: var(--spacing-2) var(--spacing-3);
    
    &:hover {
      background-color: #c82333;
    }
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-6);
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  padding: var(--spacing-4);
  text-align: center;
  
  .stat-number {
    font-size: var(--font-size-2xl);
    font-weight: 700;
    color: var(--color-secondary);
  }
  
  .stat-label {
    font-size: var(--font-size-sm);
    color: var(--color-text-light);
    margin-top: var(--spacing-1);
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const CustomersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: var(--spacing-3) var(--spacing-4);
    text-align: left;
    border-bottom: 1px solid var(--color-border);
    font-size: var(--font-size-sm);
  }
  
  th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: var(--color-text);
    position: sticky;
    top: 0;
    z-index: 1;
  }
  
  tbody tr {
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: #f8f9fa;
    }
  }
`;

const CustomerEmail = styled.button`
  background: none;
  border: none;
  color: var(--color-secondary);
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
  font-size: var(--font-size-sm);
  
  &:hover {
    color: #1f1e3a;
  }
`;

const CheckboxColumn = styled.td`
  width: 40px;
  
  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const Badge = styled.span<{ type: 'high' | 'medium' | 'low' }>`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: var(--font-size-xs);
  font-weight: 500;
  
  ${props => {
    switch (props.type) {
      case 'high':
        return 'background: #dcfce7; color: #166534;';
      case 'medium':
        return 'background: #fef3c7; color: #92400e;';
      case 'low':
        return 'background: #fee2e2; color: #991b1b;';
      default:
        return 'background: #f3f4f6; color: #374151;';
    }
  }}
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-12);
  color: var(--color-text-light);
`;

const ErrorWrapper = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: var(--border-radius);
  padding: var(--spacing-4);
  color: #dc2626;
  margin-bottom: var(--spacing-4);
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-4);
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--shadow-lg);
`;

const ModalHeader = styled.div`
  padding: var(--spacing-6);
  border-bottom: 1px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    margin: 0;
    color: var(--color-secondary);
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--color-text-light);
  
  &:hover {
    color: var(--color-text);
  }
`;

interface Customer {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  billing_address: string;
  shipping_address: string;
  total_orders: number;
  total_spent: number;
  orders_last_6_months: number;
  spent_last_6_months: number;
  first_order_date: string;
  last_order_date: string;
}

interface CustomerStatistics {
  total_customers: number;
  new_customers_this_month: number;
  active_customers_6_months: number;
  avg_customer_value: number;
  avg_orders_per_customer: number;
}

const AdminCustomers: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [statistics, setStatistics] = useState<CustomerStatistics>({
    total_customers: 0,
    new_customers_this_month: 0,
    active_customers_6_months: 0,
    avg_customer_value: 0,
    avg_orders_per_customer: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState<Set<string>>(new Set());
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerOrders, setCustomerOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [deletingCustomer, setDeletingCustomer] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [search]);

  const loadData = async (searchTerm?: string) => {
    setLoading(true);
    setError('');
    
    try {
      const searchParam = searchTerm !== undefined ? searchTerm : search;
      const [customersData, statsData] = await Promise.all([
        apiService.getCustomers({ search: searchParam || undefined }),
        apiService.getCustomerStatistics()
      ]);
      
      setCustomers(customersData);
      setStatistics(statsData);
    } catch (err: any) {
      setError(`Kunde inte ladda kunddata: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    // Force refresh - reload all data without any search filter
    await loadData('');
    // Clear any existing selections
    setSelectedCustomers(new Set());
    setSelectedCustomer(null);
  };

  const handleCustomerClick = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setLoadingOrders(true);
    
    try {
      const orders = await apiService.getCustomerOrders(customer.email);
      setCustomerOrders(orders);
    } catch (err: any) {
      console.error('Error loading customer orders:', err);
      setCustomerOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const toggleCustomerSelection = (email: string) => {
    const newSelected = new Set(selectedCustomers);
    if (newSelected.has(email)) {
      newSelected.delete(email);
    } else {
      newSelected.add(email);
    }
    setSelectedCustomers(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedCustomers.size === customers.length) {
      setSelectedCustomers(new Set());
    } else {
      setSelectedCustomers(new Set(customers.map(c => c.email)));
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCustomerValueBadge = (totalSpent: number) => {
    if (totalSpent >= 2000) return 'high';
    if (totalSpent >= 500) return 'medium';
    return 'low';
  };

  const getCustomerValueLabel = (totalSpent: number) => {
    if (totalSpent >= 2000) return 'Högvärde';
    if (totalSpent >= 500) return 'Medelvärde';
    return 'Lågvärde';
  };

  const getSelectedCustomersForEmail = () => {
    return customers
      .filter(customer => selectedCustomers.has(customer.email))
      .map(customer => ({
        email: customer.email,
        first_name: customer.first_name,
        last_name: customer.last_name
      }));
  };

  const handleDeleteCustomer = async (email: string, customerName: string) => {
    const confirmed = window.confirm(
      `Är du säker på att du vill ta bort kunden "${customerName}" (${email}) och alla deras beställningar?\n\nDetta kan inte ångras!`
    );
    
    if (!confirmed) return;

    setDeletingCustomer(email);
    try {
      // await apiService.deleteCustomer(email);
      console.log('Delete customer functionality temporarily disabled');
      
      // Remove customer from local state
      setCustomers(customers.filter(customer => customer.email !== email));
      setSelectedCustomers(prev => {
        const newSet = new Set(prev);
        newSet.delete(email);
        return newSet;
      });
      
      // Close customer details modal if it's open for this customer
      if (selectedCustomer?.email === email) {
        setSelectedCustomer(null);
      }
      
      // Refresh statistics
      const statsData = await apiService.getCustomerStatistics();
      setStatistics(statsData);
      
      setError('');
    } catch (err: any) {
      setError(`Kunde inte ta bort kund: ${err.message}`);
    } finally {
      setDeletingCustomer(null);
    }
  };

  if (loading && customers.length === 0) {
    return (
      <CustomersWrapper>
        <CustomersHeader>
          <h2>👥 Kundregister</h2>
          <p>Hantera och analysera dina kunder</p>
        </CustomersHeader>
        <LoadingWrapper>Laddar kunddata...</LoadingWrapper>
      </CustomersWrapper>
    );
  }

  return (
    <>
      <CustomersWrapper>
        <CustomersHeader>
          <h2>👥 Kundregister</h2>
          <p>Hantera och analysera dina kunder med orderhistorik och statistik</p>
          
          {error && <ErrorWrapper>{error}</ErrorWrapper>}
          
          <Stats>
            <StatCard>
              <div className="stat-number">{statistics.total_customers}</div>
              <div className="stat-label">Totala kunder</div>
            </StatCard>
            <StatCard>
              <div className="stat-number">{statistics.new_customers_this_month}</div>
              <div className="stat-label">Nya denna månad</div>
            </StatCard>
            <StatCard>
              <div className="stat-number">{statistics.active_customers_6_months}</div>
              <div className="stat-label">Aktiva senaste 6 mån</div>
            </StatCard>
            <StatCard>
              <div className="stat-number">{formatCurrency(statistics.avg_customer_value)}</div>
              <div className="stat-label">Genomsnittligt kundvärde</div>
            </StatCard>
          </Stats>
          
          <ControlsWrapper>
            <SearchInput
              type="text"
              placeholder="Sök på namn, email eller telefon..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            
            <ActionButtons>
              <Button 
                className="secondary" 
                disabled={selectedCustomers.size === 0}
                onClick={() => setShowEmailComposer(true)}
              >
                ✉️ Skicka email ({selectedCustomers.size})
              </Button>
              <Button className="primary" onClick={handleRefresh}>
                🔄 Uppdatera
              </Button>
            </ActionButtons>
          </ControlsWrapper>
        </CustomersHeader>
        
        <TableWrapper>
          <CustomersTable>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={customers.length > 0 && selectedCustomers.size === customers.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Kund</th>
                <th>Telefon</th>
                <th>Totalt antal order</th>
                <th>Totalt spenderat</th>
                <th>Senaste 6 mån</th>
                <th>Kundvärde</th>
                <th>Första order</th>
                <th>Senaste order</th>
                <th>Åtgärder</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr key={customer.email}>
                  <CheckboxColumn>
                    <input
                      type="checkbox"
                      checked={selectedCustomers.has(customer.email)}
                      onChange={() => toggleCustomerSelection(customer.email)}
                    />
                  </CheckboxColumn>
                  <td>
                    <div>
                      <CustomerEmail onClick={() => handleCustomerClick(customer)}>
                        {customer.email}
                      </CustomerEmail>
                      <div style={{ color: 'var(--color-text-light)', fontSize: '12px', marginTop: '2px' }}>
                        {customer.first_name} {customer.last_name}
                      </div>
                    </div>
                  </td>
                  <td>{customer.phone || '-'}</td>
                  <td>
                    <strong>{customer.total_orders}</strong>
                    {customer.orders_last_6_months > 0 && (
                      <div style={{ fontSize: '11px', color: 'var(--color-text-light)' }}>
                        {customer.orders_last_6_months} senaste 6 mån
                      </div>
                    )}
                  </td>
                  <td>
                    <strong>{formatCurrency(customer.total_spent)}</strong>
                  </td>
                  <td>
                    <strong>{formatCurrency(customer.spent_last_6_months)}</strong>
                    <div style={{ fontSize: '11px', color: 'var(--color-text-light)' }}>
                      {customer.orders_last_6_months} order
                    </div>
                  </td>
                  <td>
                    <Badge type={getCustomerValueBadge(customer.total_spent)}>
                      {getCustomerValueLabel(customer.total_spent)}
                    </Badge>
                  </td>
                  <td>{formatDate(customer.first_order_date)}</td>
                  <td>{formatDate(customer.last_order_date)}</td>
                  <td>
                    <Button
                      className="danger"
                      onClick={() => handleDeleteCustomer(customer.email, `${customer.first_name} ${customer.last_name}`)}
                      disabled={deletingCustomer === customer.email}
                    >
                      {deletingCustomer === customer.email ? '⏳' : '🗑️'} Ta bort
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </CustomersTable>
        </TableWrapper>
      </CustomersWrapper>
      
      {/* Customer Details Modal */}
      {selectedCustomer && (
        <ModalOverlay onClick={() => setSelectedCustomer(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>👤 Kunddetaljer - {selectedCustomer.first_name} {selectedCustomer.last_name}</h3>
              <CloseButton onClick={() => setSelectedCustomer(null)}>×</CloseButton>
            </ModalHeader>
            
            <div style={{ padding: 'var(--spacing-6)' }}>
              {loadingOrders ? (
                <LoadingWrapper>Laddar orderhistorik...</LoadingWrapper>
              ) : (
                <div>
                  <div style={{ marginBottom: 'var(--spacing-6)' }}>
                    <h4>Kontaktinformation</h4>
                    <p><strong>Email:</strong> {selectedCustomer.email}</p>
                    <p><strong>Telefon:</strong> {selectedCustomer.phone || 'Ej angivet'}</p>
                    <p><strong>Faktureringsadress:</strong> {selectedCustomer.billing_address || 'Ej angivet'}</p>
                    {selectedCustomer.shipping_address && (
                      <p><strong>Leveransadress:</strong> {selectedCustomer.shipping_address}</p>
                    )}
                  </div>
                  
                  <div style={{ marginBottom: 'var(--spacing-6)' }}>
                    <h4>Orderhistorik ({customerOrders.length} ordrar)</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '8px', borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>
                              Ordernummer
                            </th>
                            <th style={{ padding: '8px', borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>
                              Datum
                            </th>
                            <th style={{ padding: '8px', borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>
                              Status
                            </th>
                            <th style={{ padding: '8px', borderBottom: '1px solid var(--color-border)', textAlign: 'right' }}>
                              Belopp
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {customerOrders.map((order) => (
                            <tr key={order.id}>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                {order.order_number}
                              </td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                {formatDate(order.created_at)}
                              </td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <span style={{ 
                                  padding: '2px 8px', 
                                  borderRadius: '12px', 
                                  fontSize: '11px',
                                  backgroundColor: order.status === 'sent' ? '#d1fae5' : order.status === 'cancelled' ? '#fee2e2' : '#fef3c7',
                                  color: order.status === 'sent' ? '#065f46' : order.status === 'cancelled' ? '#991b1b' : '#92400e'
                                }}>
                                  {order.status}
                                </span>
                              </td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                                {formatCurrency(order.total_amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
      
      {/* Email Composer Modal */}
      {showEmailComposer && selectedCustomers.size > 0 && (
        <EmailComposer
          customers={getSelectedCustomersForEmail()}
          onClose={() => setShowEmailComposer(false)}
        />
      )}
    </>
  );
};

export default AdminCustomers;